import React, { useEffect, useState } from 'react';
import PageTransition from '../../components/PageTransition';
import SafeIcon from '../../common/SafeIcon';
import { FiPlus, FiFolder } from 'react-icons/fi';
import { supabase, TABLES } from '../../supabase/supabase';
import { useAuth } from '../../contexts/AuthContext';
import ProjectCard from '../../components/dashboard/ProjectCard';
import CreateProjectModal from '../../components/dashboard/CreateProjectModal';

const Projects = () => {
  const { activeOrg } = useAuth();
  const [loading, setLoading] = useState(true);
  const [projects, setProjects] = useState([]);
  const [isArchivedView, setIsArchivedView] = useState(false);
  const [modalState, setShowModal] = useState({ isOpen: false, editingProject: null });
  const [actionLoading, setActionLoading] = useState(false);

  useEffect(() => {
    if (activeOrg) fetchProjects();
  }, [activeOrg, isArchivedView]);

  const fetchProjects = async () => {
    try {
      setLoading(true);
      const { data: projectsData, error: pError } = await supabase
        .from(TABLES.PROJECTS)
        .select('*')
        .eq('org_id', activeOrg.id)
        .eq('status', isArchivedView ? 'archived' : 'active')
        .order('created_at', { ascending: false });

      if (pError) throw pError;

      const enrichedProjects = await Promise.all((projectsData || []).map(async (project) => {
        const { count: sCount } = await supabase.from(TABLES.SCREENER_QUESTIONS).select('*', { count: 'exact', head: true }).eq('project_id', project.id);
        const { count: tCount } = await supabase.from(TABLES.SCREENER_QUESTION_TARGETS).select('*', { count: 'exact', head: true }).eq('project_id', project.id);
        const { count: mCount } = await supabase.from(TABLES.MODULES).select('*', { count: 'exact', head: true }).eq('project_id', project.id);

        const { data: qTargets } = await supabase
          .from(TABLES.SCREENER_QUESTION_TARGETS)
          .select('question_id,target_percentage')
          .eq('project_id', project.id);

        const targetSums = {};
        qTargets?.forEach(t => {
          targetSums[t.question_id] = (targetSums[t.question_id] || 0) + parseFloat(t.target_percentage);
        });
        const hasCompleteTarget = Object.values(targetSums).some(sum => sum === 100);
        const targetsComplete = (project.target_n > 0) && hasCompleteTarget;

        const { data: qData } = await supabase
          .from(TABLES.SCREENER_QUESTIONS)
          .select(`tag_key, question_text, targets: ${TABLES.SCREENER_QUESTION_TARGETS}(category_value,target_count)`)
          .eq('project_id', project.id);

        const { data: sessionTags } = await supabase
          .from(TABLES.SEGMENT_TAGS)
          .select('tag_key,tag_value')
          .in('session_id', (
            await supabase.from(TABLES.SESSIONS).select('id').eq('project_id', project.id)
          ).data?.map(s => s.id) || []);

        let totalTarget = 0;
        let totalActual = 0;
        let healthColor = 'gray';

        const segmentationData = (qData || [])
          .filter(q => q.tag_key && q.targets?.length > 0)
          .map(q => {
            const targets = q.targets.map(t => {
              const actual = sessionTags?.filter(st => st.tag_key === q.tag_key && st.tag_value === t.category_value).length || 0;
              totalTarget += t.target_count;
              totalActual += actual;
              return { ...t, actual };
            });
            return { 
              tag_key: q.tag_key, 
              question_text: q.question_text, 
              targets 
            };
          });

        if (totalActual > 0) {
          const allMet = segmentationData.every(dim => dim.targets.every(t => t.actual >= t.target_count));
          healthColor = allMet ? 'green' : 'amber';
        }

        return {
          ...project,
          screenersCount: sCount || 0,
          targetsCount: tCount || 0,
          targetsComplete,
          modulesCount: mCount || 0,
          segmentation: segmentationData,
          totalActual,
          totalTarget: totalTarget || project.target_n || 10,
          healthColor
        };
      }));

      setProjects(enrichedProjects);
    } catch (err) {
      console.error('Error fetching projects:', err);
    } finally {
      setLoading(false);
    }
  };

  const handleProjectSubmit = async (formData) => {
    try {
      setActionLoading(true);
      if (modalState.editingProject) {
        const { error } = await supabase
          .from(TABLES.PROJECTS)
          .update({ name: formData.name, description: formData.description, target_n: formData.target_n })
          .eq('id', modalState.editingProject.id);
        if (error) throw error;
      } else {
        const { error } = await supabase.from(TABLES.PROJECTS).insert([{ name: formData.name, description: formData.description, target_n: formData.target_n, org_id: activeOrg.id, status: 'active' }]);
        if (error) throw error;
      }
      setShowModal({ isOpen: false, editingProject: null });
      fetchProjects();
    } catch (err) {
      alert(err.message);
    } finally {
      setActionLoading(false);
    }
  };

  const updateStatus = async (projectId, status) => {
    try {
      const { error } = await supabase
        .from(TABLES.PROJECTS)
        .update({ status })
        .eq('id', projectId);
      if (error) throw error;
      fetchProjects();
    } catch (err) {
      console.error(err);
    }
  };

  if (loading && projects.length === 0) {
    return (
      <div className="h-full flex items-center justify-center">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-rutabaga-pink"></div>
      </div>
    );
  }

  return (
    <PageTransition className="max-w-7xl mx-auto">
      <div className="flex items-center justify-between mb-10">
        <div>
          <h1 className="text-4xl font-black text-rutabaga-dark tracking-tight">
            {isArchivedView ? 'Archived Projects' : 'Research Projects'}
          </h1>
          <div className="mt-2">
            <p className="text-gray-500 font-medium">Manage your {activeOrg?.name} lab studies and recruitment health.</p>
            <button onClick={() => setIsArchivedView(!isArchivedView)} className="mt-1 text-sm text-gray-400 hover:text-gray-600 font-medium flex items-center gap-1 transition-colors" >
              {isArchivedView ? '← Back to Active' : 'View archived projects →'}
            </button>
          </div>
        </div>
        {!isArchivedView && (
          <button onClick={() => setShowModal({ isOpen: true, editingProject: null })} className="rutabaga-button-primary flex items-center gap-2" >
            <SafeIcon icon={FiPlus} strokeWidth={3} /> <span>New Project</span>
          </button>
        )}
      </div>

      {projects.length > 0 ? (
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
          {projects.map((project) => (
            <ProjectCard key={project.id} project={project} onArchive={updateStatus} onEdit={(p) => setShowModal({ isOpen: true, editingProject: p })} />
          ))}
        </div>
      ) : (
        <div className="flex flex-col items-center justify-center py-32 bg-white rounded-lg border border-dashed border-gray-200 mt-8 shadow-sm">
          <div className="w-24 h-24 bg-rutabaga-bg rounded-md flex items-center justify-center text-gray-300 mb-8">
            <SafeIcon icon={FiFolder} className="text-4xl" />
          </div>
          <h3 className="text-3xl font-black text-rutabaga-dark tracking-tight">No {isArchivedView ? 'archived' : ''} projects found.</h3>
          <p className="text-gray-500 mt-3 max-w-sm text-center font-medium">
            {isArchivedView ? "You haven't archived any projects yet." : "Launch your first research study to start gathering participant insights."}
          </p>
        </div>
      )}
      <CreateProjectModal isOpen={modalState.isOpen} onClose={() => setShowModal({ isOpen: false, editingProject: null })} onSubmit={handleProjectSubmit} loading={actionLoading} initialData={modalState.editingProject} />
    </PageTransition>
  );
};

export default Projects;